/**
 * Shopping Cart Context
 * Manages merch cart state across the application
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Fetch cart from server
  const fetchCart = useCallback(async () => {
    if (!token) {
      setCart({ items: [], total: 0, item_count: 0 });
      return;
    }

    try {
      const response = await axios.get(`${API}/merch/cart/items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    }
  }, [token]);

  // Load cart on mount and when user changes
  useEffect(() => {
    if (user) {
      fetchCart();
    } else {
      setCart({ items: [], total: 0, item_count: 0 });
    }
  }, [user, fetchCart]);

  // Add item to cart
  const addToCart = async (merchId, quantity = 1, size = null) => {
    if (!token) {
      toast.error('Please login to add items to cart');
      return false;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/merch/cart/add`,
        { merch_id: merchId, quantity, size },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || 'Added to cart!');
      await fetchCart();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update cart item quantity
  const updateQuantity = async (merchId, quantity, size = null) => {
    if (!token) return false;

    setLoading(true);
    try {
      await axios.put(
        `${API}/merch/cart/update`,
        { merch_id: merchId, quantity, size },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await fetchCart();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update cart');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Remove item from cart
  const removeFromCart = async (merchId, size = null) => {
    if (!token) return false;

    setLoading(true);
    try {
      const url = size 
        ? `${API}/merch/cart/item/${merchId}?size=${encodeURIComponent(size)}`
        : `${API}/merch/cart/item/${merchId}`;
      
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Removed from cart');
      await fetchCart();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove item');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Clear entire cart
  const clearCart = async () => {
    if (!token) return false;

    setLoading(true);
    try {
      await axios.delete(`${API}/merch/cart/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCart({ items: [], total: 0, item_count: 0 });
      toast.success('Cart cleared');
      return true;
    } catch (error) {
      toast.error('Failed to clear cart');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Checkout cart via Stripe
  const checkout = async (shippingAddress = null) => {
    if (!token) {
      toast.error('Please login to checkout');
      return null;
    }

    if (cart.items.length === 0) {
      toast.error('Cart is empty');
      return null;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/merch/cart/checkout`,
        { shipping_address: shippingAddress },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
      
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    cart,
    loading,
    isOpen,
    setIsOpen,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    checkout,
    refreshCart: fetchCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
